import { Component, computed, input, ChangeDetectionStrategy } from '@angular/core';
import { ApiDateTime, toCalendarDay } from '@shared/models/dateTypes';
import { IconLabelComponent } from '../icon-label/icon-label.component';
import { CapitalizeFirstPipe } from '@shared/utilities/capitalize-first.pipe';
import { DanishDatePipe } from '@shared/utilities/danish-date.pipe';

/**
 * DateIconLabelVariationsComponent
 *
 * Composite component that displays date and time information using icon-label components.
 * Renders 2 stacked icon-labels with different icons and formats based on date span.
 *
 * Supports two variations:
 * - sameDay: Shows date on first line, time range on second line
 * - multiDay: Shows start date+time on first line, end date+time on second line
 *
 * The component automatically determines the variation based on comparing
 * start and end dates (ignoring time component).
 *
 * Inputs:
 * - startDate: Start date of the event (Date object or ISO date string)
 * - endDate: End date of the event (Date object or ISO date string)
 *
 * Example:
 * <app-date-icon-label-variations
 *   [startDate]="bp.start"
 *   [endDate]="bp.end"
 * />
 */
type DateSpan = 'multiDay' | 'sameDay';
@Component({
  selector: 'app-date-icon-label-variations',
  imports: [DanishDatePipe, IconLabelComponent, CapitalizeFirstPipe],
  templateUrl: './date-icon-label-variations.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './date-icon-label-variations.component.scss',
})
export class DateIconLabelVariationsComponent {

  /** Start of the event, as the API writes it. */
  startDate = input.required<ApiDateTime>();

  /** End of the event, as the API writes it. */
  endDate = input.required<ApiDateTime>();

  /**
   * Whether the event runs past midnight.
   *
   * Compared as calendar days, which is pure string work — `toCalendarDay` takes the day off each
   * end without building an instant, so the answer cannot depend on where the reader sits. This
   * used to accept a `Date` too and parse the string arm into an instant; the `Date` arm is gone,
   * because the one caller that supplied one was handing over a browser-local reading that the
   * display then re-read in Copenhagen.
   */
  dateSpan = computed<DateSpan>(() => {
    const start = this.startDate();
    const end = this.endDate();

    if (!start || !end) {
      return 'sameDay';
    }

    return toCalendarDay(start) === toCalendarDay(end) ? 'sameDay' : 'multiDay';
  });
}
